import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, Clock, Calculator } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [balance, setBalance] = useState(0);
  const [busy, setBusy] = useState("");
  const [calc, setCalc] = useState({ plan_id: "", amount: "1000" });

  const load = async () => {
    const [p, inv, dash] = await Promise.all([
      api.get("/plans"), api.get("/investments"), api.get("/dashboard"),
    ]);
    setPlans(p.data.plans);
    setCalc((c) => (c.plan_id ? c : { ...c, plan_id: p.data.plans[0]?.id || "" }));
    setInvestments(inv.data.investments);
    setBalance(dash.data.balance.available);
  };
  useEffect(() => { load(); }, []);

  const invest = async (plan) => {
    const amount = parseFloat(amounts[plan.id]);
    if (!amount || amount <= 0) return toast.error("Enter an amount");
    setBusy(plan.id);
    try {
      await api.post("/invest", { plan_id: plan.id, amount });
      toast.success(`Invested $${amount} in the ${plan.name} plan!`);
      setAmounts({ ...amounts, [plan.id]: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy("");
    }
  };

  const calcPlan = plans.find((p) => p.id === calc.plan_id) || plans[0];
  const calcAmt = parseFloat(calc.amount) || 0;
  const calcDaily = calcPlan ? (calcAmt * calcPlan.daily_roi_pct) / 100 : 0;
  const calcTotal = calcPlan ? calcDaily * calcPlan.duration_days : 0;
  const calcReturn = calcAmt + calcTotal;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Investment Plans</h1>
          <p className="text-sm text-slate-400 mt-1">Available balance: <span className="text-emerald-400 font-mono font-semibold">${fmt(balance)}</span></p>
        </div>
      </div>

      <div className="card-surface rounded-2xl p-6 mb-8" data-testid="roi-calculator">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold">ROI Calculator</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400">Select Plan</label>
              <div className="flex gap-2 mt-1.5">
                {plans.map((p) => (
                  <button key={p.id} data-testid={`calc-plan-${p.id}`} onClick={() => setCalc({ ...calc, plan_id: p.id })}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${calc.plan_id === p.id ? "bg-emerald-500 text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Investment Amount (USDT)</label>
              <Input data-testid="calc-amount-input" type="number" value={calc.amount}
                onChange={(e) => setCalc({ ...calc, amount: e.target.value })}
                className="mt-1.5 bg-white/5 border-slate-700" />
            </div>
            {calcPlan && (
              <div className="text-xs text-slate-500">{calcPlan.daily_roi_pct}% daily · {calcPlan.duration_days} days · principal returned at maturity</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-xs text-slate-400">Daily Profit</div>
              <div className="text-xl font-extrabold font-mono text-emerald-400 mt-1" data-testid="calc-daily">${fmt(calcDaily)}</div>
            </div>
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-xs text-slate-400">Total ROI ({calcPlan?.duration_days || 0}d)</div>
              <div className="text-xl font-extrabold font-mono text-emerald-400 mt-1" data-testid="calc-total-roi">${fmt(calcTotal)}</div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 col-span-2">
              <div className="text-xs text-slate-400">Total Return (principal + ROI)</div>
              <div className="text-2xl font-extrabold font-mono text-emerald-400 mt-1" data-testid="calc-total-return">${fmt(calcReturn)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {plans.map((p) => (
          <div key={p.id} className="card-surface rounded-2xl p-6" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400 uppercase tracking-wider">{p.name}</span>
              <TrendingUp className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="text-4xl font-extrabold font-mono text-emerald-400">{p.daily_roi_pct}%</div>
            <div className="text-xs text-slate-500 mb-4">daily ROI · {p.duration_days} days</div>
            <div className="text-sm text-slate-300 mb-4">${fmt(p.min_amount)} – ${fmt(p.max_amount)} USDT</div>
            <Input data-testid={`invest-amount-${p.id}`} type="number" placeholder="Amount (USDT)"
              value={amounts[p.id] || ""} onChange={(e) => setAmounts({ ...amounts, [p.id]: e.target.value })}
              className="bg-white/5 border-slate-700 mb-3" />
            <Button data-testid={`invest-plan-${p.id}-button`} disabled={busy === p.id} onClick={() => invest(p)}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
              {busy === p.id ? "Investing…" : "Invest Now"}
            </Button>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-bold mb-4">My Active Investments</h2>
      {investments.length === 0 ? (
        <div className="card-surface rounded-2xl p-8 text-center text-slate-400" data-testid="no-investments">
          No investments yet. Choose a plan above to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {investments.map((inv) => (
            <div key={inv.id} className="card-surface rounded-2xl p-5" data-testid={`investment-${inv.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-semibold">{inv.plan_name}</span>
                  <span className="ml-3 font-mono text-emerald-400">${fmt(inv.amount)}</span>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${inv.status === "active" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-500/10 text-slate-400"}`}>
                  {inv.status}
                </span>
              </div>
              <Progress value={(inv.days_paid / inv.duration_days) * 100} className="h-2 mb-2" />
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Day {inv.days_paid}/{inv.duration_days}</span>
                <span>Earned: <span className="text-emerald-400 font-mono">${fmt(inv.total_earned)}</span></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
